/**
 * BigCommerce injects the consent manager without stable automation hooks.
 * Tag the primary accept control once so E2E can use getByTestId('consent-manager-accept').
 */
const CONSENT_ACCEPT_TEST_ID = "consent-manager-accept";

/** BC consent UI is mounted under these roots (see tagConsentManagerAcceptButtons). */
const CONSENT_CONTEXT_SELECTOR =
  "[data-consent-manager-dialog], #consent-manager";

/**
 * Returns true only when a mutation might add or change consent manager markup.
 *
 * @param {MutationRecord[]} mutations
 */
function mutationsMightAffectConsentManager(mutations) {
  /** @param {Element} el */
  const isInConsentContext = (el) =>
    Boolean(el?.closest?.(CONSENT_CONTEXT_SELECTOR));

  for (const m of mutations) {
    const target = m.target;
    if (target?.nodeType === Node.ELEMENT_NODE) {
      if (isInConsentContext(/** @type {Element} */ (target))) {
        return true;
      }
    }

    for (const n of m.addedNodes) {
      if (n.nodeType !== Node.ELEMENT_NODE) {
        continue;
      }
      const el = /** @type {Element} */ (n);
      if (
        isInConsentContext(el) ||
        el.matches?.("[data-consent-manager-dialog], #consent-manager") ||
        el.querySelector?.("[data-consent-manager-dialog], #consent-manager")
      ) {
        return true;
      }
    }
  }
  return false;
}

function tagAcceptButtonForTesting(element) {
  if (element.dataset.testid === CONSENT_ACCEPT_TEST_ID) {
    return;
  }

  const action = element.dataset.action || "";
  if (/accept/i.test(action)) {
    element.dataset.testid = CONSENT_ACCEPT_TEST_ID;
  }
}

function tagConsentManagerAcceptButtons() {
  const roots = document.querySelectorAll(
    "[data-consent-manager-dialog], #consent-manager"
  );

  roots.forEach((root) => {
    root
      .querySelectorAll("button, [role='button']")
      .forEach(tagAcceptButtonForTesting);
  });
}

export default function initConsentManagerTestIds() {
  tagConsentManagerAcceptButtons();

  if (typeof MutationObserver === "undefined") {
    return;
  }

  const observer = new MutationObserver((mutations) => {
    if (!mutationsMightAffectConsentManager(mutations)) {
      return;
    }
    tagConsentManagerAcceptButtons();
  });

  // Prefer `document.body` over `document.documentElement` to avoid `<head>` churn;
  // consent overlays are injected into the body subtree. Callback filters mutations.
  observer.observe(document.body, { childList: true, subtree: true });
}
